import { Injectable } from '@angular/core';
import { CompoundFilter, Filter, IqlQueryOperator, LeafFilter } from '@itonics/filters';

import { BehaviorSubject, Observable, tap } from 'rxjs';

import { FilterByCriteria } from '@innovation-os-ng/common';
import { iptc } from '../../../../shared/dictionaries/iptc';
import { SignalsType, TopicDictionary } from '../../../../shared/model/types';
import { DocumentService } from '../../../../shared/modules/shared-services/services/document.service';
import { AGGREGATION_TYPES, URI } from '../../../constants/constants';
import { AggregationResults, FeedWidgetService } from '../../../contracts/feed-widget.service';
import { Document, FeedPart, FeedPartDetail, FilterQuery } from '../../../interfaces/feed';
import { EvaluationCriteria } from '../../../model/model';
import { EvaluationService } from '../../../services/evaluation.service';
import { FilterQueryBuilder } from '../../../services/filter-query-builder.service';

// Minimum Rating count for filtration
const MIN_FILTRATION_COUNT: number = 5;

// Default Evaluation Criteria
const DEFAULT_EVALUATION_CRITERIA: EvaluationCriteria = {
  criterionUri: 'relevance',
  criterionValue: 'None',
  minRatingCountForFiltration: MIN_FILTRATION_COUNT
};

@Injectable({
  providedIn: 'root'
})
export class FeedFilterService {
  private loading = new BehaviorSubject<boolean>(false);
  private loadingItems: Array<string> = [];

  /**
   * Allows retrieval of loading state observable
   *
   * @returns an observable of the loading state
   */
  public get isLoading(): Observable<boolean> {
    return this.loading.asObservable();
  }

  constructor(
    private readonly evaluationService: EvaluationService,
    private readonly feedWidgetService: FeedWidgetService,
    private readonly documentService: DocumentService
  ) {}

  /**
   * Provides the updated query after evaluating all related parameters.
   *
   * @param signalUris - Array for uri of signals.
   * @param filter - Filter object.
   * @param aggregationFor - Aggregation option as type or source.
   * @returns - this will return the updated query after evaluating all related parameters
   */
  public buildFeedQuery(signalUris: Array<string>, filter?: Filter, aggregationFor?: Array<string>): FilterQuery {
    const filterQuery: FilterQuery = new FilterQueryBuilder()
      .query(this.buildFilterQuery(signalUris, filter))
      .aggregations(aggregationFor ? aggregationFor : [AGGREGATION_TYPES.TYPE])
      .limit(1)
      .build();
    return filterQuery;
  }

  /**
   * Prepares a compound filter with provided signal uri or filter.
   *
   * @param signalUris - Uri for the signal
   * @param filter - Filter object
   * @returns Returns the compound filter with uri or filter set.
   */
  public buildFilterQuery(signalUris: Array<string>, filter?: Filter): CompoundFilter {
    const compoundFilter: CompoundFilter = new CompoundFilter(IqlQueryOperator.AND);

    if (filter) {
      compoundFilter.addFilter(filter);
    }
    if (signalUris) {
      compoundFilter.addFilter(new LeafFilter(URI).setValues(signalUris));
    }

    return compoundFilter;
  }

  /**
   * Provides the feed filter from the filter object.
   *
   * @returns Filter related to feed.
   */
  public getFeedFilter(filter: CompoundFilter, filterIdentifier: string): CompoundFilter {
    return filter?.getQuery(filterIdentifier) as CompoundFilter;
  }

  /**
   * We need to select endpoint here.
   * the pipe added to the return observable is used to update the loading state
   *
   * @param feedQuery - Filter.
   * @param loadFeedByEvaluation - Flag to whether load feed by evaluation.
   * @param filterBy - Filter option.
   * @returns - Provides the feed data according to the "loadFeedByEvaluation" flag type.
   */
  public getFeedEndpoint(
    feedQuery: FilterQuery,
    loadFeedByEvaluation: boolean,
    filterBy: FilterByCriteria
  ): Observable<AggregationResults<Document>> {
    let rtnObs: Observable<AggregationResults<Document>>;

    this.setLoading(feedQuery.aggregations.join(','));

    if (loadFeedByEvaluation) {
      // Add required criterions for loading feed by evaluation
      const filterCriterion = this.getFeedEvaluationCriteria(filterBy);
      if (filterCriterion) {
        feedQuery = { ...feedQuery, ...filterCriterion };
      }
      rtnObs = this.evaluationService.getFeedsByEvaluation(feedQuery);
    } else {
      rtnObs = this.feedWidgetService.getFeeds(feedQuery);
    }

    return rtnObs.pipe(
      tap({
        next: () => {
          this.setLoading(feedQuery.aggregations.join(','), true);
        },
        error: (err) => {
          this.setLoading(feedQuery.aggregations.join(','), true);
        }
      })
    );
  }

  /**
   * Provides the selecton filter with applied selected feed part filter.
   *
   * Note: Just pass the selection filter and reference will be mutated.
   *
   * @param event - User Filter action event.
   * @param selectionFilter - Selection filter in which we need to apply the feed part action filter.
   */
  public applyFeedPartActionToSelectionFilter = (event: FeedPartDetail, selectionFilter: CompoundFilter): void => {
    let property: string = event.type;

    if (event.type === FeedPart.TOPIC) {
      property = SignalsType.TOPICS;
    } else if (event.type === FeedPart.Entity) {
      property = `${SignalsType.ENTITY}.${event.group}.label`;
    } else if (event.type === FeedPart.CATEGORY) {
      property = SignalsType.CATEGORY;
      const iptcDetail: TopicDictionary = iptc.find((category) => category.label === event.label?.toLocaleLowerCase());
      if (iptcDetail) {
        event.label = iptcDetail.hierarchicalLabel;
      }
    }

    // Apply the filter in the selection filter.
    this.documentService.documentPropertyClicked(selectionFilter, property, event.label || event.uri);
  };

  /**
   * Normally we load relevant documents only, but check if filter by is passed.
   *
   * @param filterBy - Filter options.
   * @returns - provides the document filtering data.
   */
  private getFeedEvaluationCriteria(filterBy: FilterByCriteria): EvaluationCriteria {
    if (filterBy) {
      return {
        criterionUri: filterBy.criterionUri,
        criterionValue: filterBy.criterionValue,
        minRatingCountForFiltration: MIN_FILTRATION_COUNT
      };
    } else {
      return DEFAULT_EVALUATION_CRITERIA;
    }
  }

  /**
   * Pass an identifier to this to update the loading state, add done true to remove it.
   * Loading will remain true as long as their are items in the loadingItems array.
   *
   * @param identifier - a string to identify the loading item
   * @param done - flag to mark an item as done and remove it from the loading items array
   */
  private setLoading(identifier: string, done = false) {
    if (done) {
      this.loadingItems = this.loadingItems.filter((id) => id !== identifier);
    } else {
      if (!this.loadingItems.includes(identifier)) {
        this.loadingItems.push(identifier);
      }
    }

    this.loading.next(this.loadingItems.length > 0);
  }
}
